import { LoadingButton } from '@mui/lab';
import {
  Button,
  IconButton,
  Stack,
  styled,
  TextField,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { useContext, useState } from 'react';
import Iconify from 'src/components/iconify';
import { CandidateProfileContext } from '../context/candidate-profile-context';
import { useAuthContext } from 'src/auth/hooks';
import { getOrganizationId } from 'src/services/user.service';
import { addNote } from 'src/services/role.service';
import ReactAudioPlayer from 'react-audio-player';
import FilesPreview from './files-preview';
import { uploadFile } from 'src/services/aws.service';
import { v4 as uuidv4 } from 'uuid';
import PropTypes from 'prop-types';

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const filterFilesUnder50MB = files => {
  const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB in bytes
  const filteredFiles = [];
  const oversizedFiles = [];

  files.forEach(file => {
    if (file.size <= MAX_FILE_SIZE) {
      filteredFiles.push(file);
    } else {
      oversizedFiles.push(file.name);
    }
  });

  return { filteredFiles, oversizedFiles };
};

export default function AddCommentBox({ comment, mode = 'CREATE', setMode }) {
  // hooks
  const { user } = useAuthContext();
  const orgId = getOrganizationId(user);
  const {
    obj: candidateDetails,
    jobId,
    refetchWithoutLoading,
  } = useContext(CandidateProfileContext);
  const { enqueueSnackbar } = useSnackbar();

  const candidateUserId = candidateDetails?.candidate_info?.user_id;

  // states
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [commentText, setCommentText] = useState(comment?.note_text || '');
  const [files, setFiles] = useState(comment?.attachments || []);

  // functions
  const addNewNote = async () => {
    try {
      const trimmedCommentText = commentText.trim();
      if (!trimmedCommentText) {
        enqueueSnackbar('Comment cannot be empty!', {
          variant: 'warning',
        });
        return;
      }

      setIsSubmitting(true);

      const payload = {
        candidate_note_type: 'COMMENT',
        note_text: trimmedCommentText,
      };

      if (mode === 'EDIT') {
        payload.note_id = comment.candidate_note_id;
      }

      if (files.length) {
        const fileUrls = await Promise.all(
          files.map(async file => {
            if (typeof file === 'string') return file;

            const extension = file.name.split('.').pop();
            const url = await uploadFile(
              process.env.REACT_APP_RESUME_BUCKET,
              file,
              `notes/${candidateUserId}/${uuidv4()}.${extension}`,
              file.type,
            );
            return url;
          }),
        );

        payload.attachments = fileUrls;
      }

      const response = await addNote(orgId, jobId, candidateUserId, payload);

      if (response.success) {
        refetchWithoutLoading();
        enqueueSnackbar(
          `Comment ${mode === 'CREATE' ? 'added' : 'edited'} successfully!`,
        );
      } else throw new Error(response.message);
    } catch (err) {
      console.log(err);
      enqueueSnackbar('Something went wrong!', {
        variant: 'error',
      });
    } finally {
      setIsSubmitting(false);
      setCommentText('');
      setFiles([]);
      if (setMode) {
        setMode('VIEW');
      }
    }
  };

  return (
    <Stack spacing="12px">
      <Stack spacing="8px">
        <TextField
          value={commentText}
          onChange={e => setCommentText(e.target.value)}
          fullWidth
          multiline
          maxRows={8}
          placeholder="Please comment here..."
          variant="outlined"
          InputProps={{
            endAdornment: (
              <IconButton role={undefined} component="label" tabIndex={-1}>
                <Iconify icon="tabler:paperclip" />
                <VisuallyHiddenInput
                  type="file"
                  multiple
                  onChange={e => {
                    const { filteredFiles, oversizedFiles } =
                      filterFilesUnder50MB(Array.from(e.target.files));

                    if (oversizedFiles.length > 0) {
                      const oversizedFilesMessage = `The following files exceeded the 50MB limit and were not selected: ${oversizedFiles.join(
                        ', ',
                      )}`;
                      enqueueSnackbar(oversizedFilesMessage, {
                        variant: 'warning',
                      });
                    }

                    setFiles(filteredFiles);
                  }}
                />
              </IconButton>
            ),
          }}
        />
        <FilesPreview files={files} setFiles={setFiles} mode={mode} />
      </Stack>
      <Stack direction="row" spacing="8px">
        {mode === 'EDIT' && (
          <Button
            variant="soft"
            size="small"
            sx={{
              borderRadius: 35,
              width: 90,
              px: '17.5px',
              py: '6px',
            }}
            onClick={() => {
              setMode('VIEW');
            }}
          >
            Cancel
          </Button>
        )}
        <LoadingButton
          loading={isSubmitting}
          onClick={addNewNote}
          variant="contained"
          size="small"
          sx={{
            borderRadius: 35,
            width: 90,
            px: '17.5px',
            py: '6px',
          }}
        >
          {mode === 'EDIT' ? 'Save' : 'Comment'}
        </LoadingButton>
      </Stack>
    </Stack>
  );
}

AddCommentBox.propTypes = {
  comment: PropTypes.object,
  mode: PropTypes.string,
  setMode: PropTypes.func,
};
